import React, { useCallback, memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { media } from '../../styles/media';
import { ReactComponent as TDMLogo } from '../common/icons/TDM_main_2.svg';
import { setReturnPoint } from '../common/ScrollNavigator';
import { useNavigate } from 'react-router-dom';

// Общие стили для повторяющихся эффектов
const commonTransition = css`
  transition: all 0.3s ease;
`;

// Создаем базовые стили для мобильного хедера
const mobileHeaderStyles = css`
  @media (max-width: 768px) {
    background: #1ABC9C;
    backdrop-filter: none;
  }
`;

const mobileTextStyles = css`
  @media (max-width: 768px) {
    color: white;
  }
`;

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    top: env(safe-area-inset-top, 44px); // Отступ на высоту статус-бара
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 5%;
  max-width: 1440px;
  margin: 0 auto;
  gap: 16px;
  
  a, button, select {
    ${mobileTextStyles}
  }
`;

const LogoWrapper = styled.div`
  width: 300px;
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: auto;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  ${media.xl`
    width: 270px;
  `}
  
  ${media.lg`
    width: 240px;
  `}
  
  ${media.md`
    width: 210px;
  `}

  ${media.sm`
    width: 180px;
  `}
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-shrink: 0;
  
  a {
    color: #030236;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      color: #00B8D4;
    }
  }
  
  ${media.lg`
    display: none;
  `}
`;

// Create a wrapper for right-aligned elements
const RightAlignedItems = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
`;

// Renaming LanguageSwitch to LanguageSelectorWrapper for clarity
const LanguageSelectorWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  // Ensure it's displayed on mobile viewports
  ${media.lg` // Assuming media.lg targets mobile viewports
    display: inline-flex; // Explicitly set display 
  `}
`;

// Styling the select element
const LanguageDropdown = styled.select`
  border: none;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  color: #030236;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.3s ease;

  &:hover {
    opacity: 1;
    color: #00B8D4;
  }

  &:focus {
    outline: none;
  }

  option {
    background-color: white; // Ensure dropdown options have a background
    color: #030236;
  }

  // Apply mobile text color styles
  ${mobileTextStyles}

  // Override mobile text color for options if needed
  @media (max-width: 768px) {
    display: inline-block; // Ensure select is also displayed
    color: #00B8D4; // Use hover color for mobile view
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 4px 8px;
    option {
        color: #030236;
        background-color: white;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 20px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  
  span {
    display: block;
    width: 100%;
    height: 2px;
    background-color: #030236;
    ${commonTransition}
  }
  
  ${media.lg`
    display: flex;
  `}
  
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      span:nth-child(1) {
        transform: translateY(6px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-6px) rotate(-45deg);
      }
    `}
`;

const MobileMenu = styled.div`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1500;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const NavLinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NavLink = styled.a`
  color: #030236;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #00B8D4;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  width: 300px;
  cursor: pointer;
  
  svg {
    width: 100%;
    height: auto;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  ${media.xl`
    width: 270px;
  `}
  
  ${media.lg`
    width: 240px;
  `}
  
  ${media.md`
    width: 210px;
  `}

  ${media.sm`
    width: 180px;
  `}
`;

// Добавляем новый компонент для статус-бара
const StatusBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top, 44px);
    background-color: #00B8D4;
    z-index: 1001;

    /* Специфичные стили для Yandex Browser */
    @supports (-webkit-appearance: none) and (not (-ms-ime-align: auto)) {
      background-color: #00B8D4 !important;
      -webkit-appearance: none;
    }
  }
`;

// Define available languages
const availableLanguages = [
  { code: 'en', name: 'EN' },
  { code: 'ru', name: 'RU' },
  { code: 'zh-CN', name: 'ZH' },
  { code: 'hi', name: 'HI' },
  { code: 'fr', name: 'FR' },
  { code: 'pt-BR', name: 'PT' },
  { code: 'es', name: 'ES' },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false); // Add state for mobile menu

  // Add toggle function for mobile menu
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const changeLanguage = useCallback((lng) => {
    i18n.changeLanguage(lng);
  }, [i18n]);

  const handleMenuClick = useCallback((sectionId) => (e) => {
    e.preventDefault();
    setReturnPoint('header');
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = document.querySelector('header').offsetHeight;
      const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false); // Close menu on link click
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <>
      <StatusBar />
      <HeaderWrapper>
        <HeaderContainer>
          <LogoWrapper onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <TDMLogo />
          </LogoWrapper>
          
          {/* Wrapper for ALL Right-Aligned Items */}
          <RightAlignedItems>
            {/* Move Desktop Navigation INSIDE RightAlignedItems */}
            <Navigation>
              <a href="#about" onClick={handleMenuClick('about')}>
                {t('navigation.about')}
              </a>
              <a href="#roadmap" onClick={handleMenuClick('roadmap')}>
                {t('navigation.roadmap')}
              </a>
              <a href="#gifts" onClick={handleMenuClick('gifts')}>
                {t('navigation.join')}
              </a>
            </Navigation>

            <LanguageSelectorWrapper>
              <LanguageDropdown
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
              >
                {availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </LanguageDropdown>
            </LanguageSelectorWrapper>

            {/* Mobile Menu Button (display controlled by media query in its style) */}
            <MenuButton $isOpen={isMenuOpen} onClick={toggleMenu}>
              <span></span>
              <span></span>
              <span></span>
            </MenuButton>
          </RightAlignedItems>
          
        </HeaderContainer>
        
        {/* Restore MobileMenu */}
        <MobileMenu $isOpen={isMenuOpen}>
          <NavLinksWrapper>
            <NavLink href="#about" onClick={handleMenuClick('about')}>{t('navigation.about')}</NavLink>
            <NavLink href="#roadmap" onClick={handleMenuClick('roadmap')}>{t('navigation.roadmap')}</NavLink>
            <NavLink href="#gifts" onClick={handleMenuClick('gifts')}>{t('navigation.join')}</NavLink>
          </NavLinksWrapper>
        </MobileMenu>

      </HeaderWrapper>
    </>
  );
};

export default memo(Header); 
